import React, { useRef, useState } from "react"
import { COLORS } from "../../../utilities/Constants"
import { getPdfFromFile } from "../../../utilities/Addons"
import CustomModal from "./CustomModal"

const PdfIcon = ({ size = 28 }) => {
	const h = Math.round((size * 34) / 28)
	return (
		<svg
			width={size}
			height={h}
			viewBox="0 0 28 34"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
		>
			<rect
				x="0.5"
				y="0.5"
				width="27"
				height="33"
				rx="3.5"
				fill="#FEF2F2"
				stroke="#FECACA"
			/>
			<path
				d="M17 1V7C17 7.55 17.45 8 18 8H24"
				stroke="#FECACA"
				strokeWidth="1.5"
			/>
			<path
				d="M17 1L23 7"
				stroke="#FECACA"
				strokeWidth="1.5"
				strokeLinecap="round"
			/>
			<rect x="4" y="14" width="20" height="12" rx="2" fill="#DC2626" />
			<text
				x="14"
				y="23"
				textAnchor="middle"
				fontSize="7"
				fontWeight="700"
				fill="white"
				fontFamily="sans-serif"
			>
				PDF
			</text>
		</svg>
	)
}

export const PdfTile = ({
	doc,
	onOpen,
	onDelete,
	readOnly = false,
	size = 72,
	height,
}) => {
	const [hovered, setHovered] = useState(false)
	const h = height || Math.round((size * 80) / 72)
	return (
		<div
			data-testid="pdf-tile"
			onMouseEnter={() => setHovered(true)}
			onMouseLeave={() => setHovered(false)}
			onClick={onOpen}
			title={doc?.name || ""}
			style={{
				position: "relative",
				width: size,
				height: h,
				borderRadius: 10,
				border: `1px solid ${COLORS.dangerBorder}`,
				backgroundColor: COLORS.dangerLight,
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				justifyContent: "center",
				gap: 4,
				cursor: "pointer",
				overflow: "hidden",
			}}
		>
			<PdfIcon size={Math.round(size * 0.39)} />
			<span
				style={{
					fontSize: 9,
					color: COLORS.danger,
					fontWeight: 500,
					maxWidth: size - 8,
					overflow: "hidden",
					textOverflow: "ellipsis",
					whiteSpace: "nowrap",
					padding: "0 4px",
					textAlign: "center",
				}}
			>
				{doc?.name || ""}
			</span>

			{hovered && !readOnly && onDelete && (
				<div
					style={{
						position: "absolute",
						top: 0,
						right: 0,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
					}}
				>
					<button
						onClick={(e) => {
							e.stopPropagation()
							onDelete()
						}}
						style={{
							width: 26,
							height: 26,
							borderRadius: "50%",
							border: "none",
							backgroundColor: COLORS.danger,
							color: "#fff",
							fontSize: 16,
							cursor: "pointer",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							fontWeight: 300,
						}}
					>
						×
					</button>
				</div>
			)}
		</div>
	)
}

export const PdfSlot = ({
	label,
	doc,
	onUpload,
	onDelete,
	onGenerate = () => {},
	isUpload = true,
	readOnly = false,
	confirmDelete = true,
	confirmText = null,
}) => {
	const inputRef = useRef(null)
	const [confirmOpen, setConfirmOpen] = useState(false)
	const requestDelete = () =>
		confirmDelete ? setConfirmOpen(true) : onDelete()
	const handleFile = (e) => {
		const file = e.target.files[0]
		if (!file) return
		onUpload(getPdfFromFile(file))
		e.target.value = ""
	}

	return (
		<>
			<div
				style={{
					display: "flex",
					flexDirection: "column",
					gap: 6,
					alignItems: "center",
				}}
			>
				<span
					style={{
						fontSize: 11,
						fontWeight: 500,
						color: COLORS.textLight,
						textTransform: "uppercase",
						letterSpacing: "0.06em",
					}}
				>
					{label}
				</span>

				{doc ? (
					<PdfTile
						doc={doc}
						onOpen={() => window.open(doc.url)}
						onDelete={readOnly ? undefined : requestDelete}
						readOnly={readOnly}
					/>
				) : readOnly ? (
					<div
						style={{
							width: 72,
							height: 80,
							borderRadius: 10,
							border: `1px dashed ${COLORS.border}`,
							backgroundColor: COLORS.bg,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							color: COLORS.textLight,
							fontSize: 18,
						}}
					>
						—
					</div>
				) : (
					<div
						onClick={
							isUpload
								? () => inputRef.current?.click()
								: onGenerate
						}
						style={{
							width: 72,
							height: 80,
							borderRadius: 10,
							border: `1px dashed ${COLORS.borderStrong}`,
							backgroundColor: COLORS.bg,
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							justifyContent: "center",
							gap: 4,
							cursor: "pointer",
							transition: "all 0.15s",
						}}
						onMouseEnter={(e) =>
							(e.currentTarget.style.backgroundColor =
								COLORS.surface)
						}
						onMouseLeave={(e) =>
							(e.currentTarget.style.backgroundColor = COLORS.bg)
						}
					>
						<span style={{ fontSize: 20, color: COLORS.textLight }}>
							+
						</span>
						<span
							style={{
								fontSize: 9,
								color: COLORS.textLight,
								textAlign: "center",
								lineHeight: 1.3,
							}}
						>
							{isUpload ? "Ajouter" : "Générer"}
							<br />
							PDF
						</span>
					</div>
				)}

				<input
					ref={inputRef}
					type="file"
					accept="application/pdf"
					onChange={handleFile}
					style={{ display: "none" }}
				/>
			</div>
			{confirmOpen && (
				<CustomModal
					title={
						confirmText || `Supprimer ${label || "le document"} ?`
					}
					onClose={() => setConfirmOpen(false)}
					showCloseButton={false}
					buttons={[
						{
							title: "Annuler",
							styleName: "secondary",
							onClick: () => setConfirmOpen(false),
						},
						{
							title: "Supprimer",
							styleName: "danger",
							onClick: () => {
								setConfirmOpen(false)
								onDelete()
							},
						},
					]}
				>
					<div style={{ color: COLORS.textMuted, fontSize: 14 }}>
						Cette action est définitive.
					</div>
				</CustomModal>
			)}
		</>
	)
}
